/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { stepsRules } from '@VALIDATION/helpers/validateSteps';

import GenerationSteps from '@CONSTANTS/GENERATION_STEPS.constant';

import { GenerationMode } from '@PAGES/manager/options/Options.page';
import { Expiration } from '@PAGES/manager/download-document/DownloadDocument.page';

import isStepDataEqual from './helpers/isStepDataEqual';

interface BaseStep {
    id: GenerationSteps;

    isComplete: boolean;
}

export type DocumentStepData = {
    id: number;
    name: string;
} | null;

export interface DocumentStep extends BaseStep {
    data: DocumentStepData;
}

export type TypeStepData = {
    id: number;
    name: string;
} | null;

export interface TypeStep extends BaseStep {
    data: TypeStepData;
}

export type BrandStepData = {
    id: number;
    name: string;
} | null;

export interface BrandStep extends BaseStep {
    data: BrandStepData;
}

interface LocationStep extends BaseStep {
    data: BrandStepData;
}

type RegionStepData = {
    id: number;
    name: string;
} | null;

export interface RegionStep extends BaseStep {
    data: RegionStepData;
}

type AreaStepData = {
    id: number;
    name: string;
} | null;

interface AreaStep extends BaseStep {
    data: AreaStepData;
}

type CountryStepData = {
    id: number;
    name: string;
} | null;

export interface CountryStep extends BaseStep {
    data: CountryStepData;
}

export type OptionsStepData = {
    id: number;
    propertyName: string;
};

export interface OptionsStep extends BaseStep {
    data: OptionsStepData;
}

export type SelectContentStepData = {
    chaptersIds: number[];
    sectionsIds: number[];
    variantsIds: number[];
    tagsIds: number[];
} | null;

export interface SelectContentStep extends BaseStep {
    data: SelectContentStepData;
}

export type DownloadDocumentStepData = {
    expirationTime: Expiration;
    watermarkText: string;
    link: string;
} | null;

export interface DownloadDocumentStep extends BaseStep {
    data: DownloadDocumentStepData;
}

export type DocumentGenerationStepsStep =
    DocumentStep
    | TypeStep
    | BrandStep
    | LocationStep
    | RegionStep
    | AreaStep
    | CountryStep
    | OptionsStep
    | SelectContentStep
    | DownloadDocumentStep;

export interface DocumentGenerationStepsState {
    steps: DocumentGenerationStepsStep[];
}

export type DocumentGenerationStepsDispatchParamsApply = DocumentGenerationStepsStep;

const initialState: DocumentGenerationStepsState = {
    steps: [
        {
            id: GenerationSteps.DOCUMENT,
            isComplete: false,
            data: null,
        } as DocumentStep,
        {
            id: GenerationSteps.TYPE,
            isComplete: false,
            data: null,
        } as TypeStep,
        {
            id: GenerationSteps.BRAND,
            isComplete: false,
            data: null,
        } as BrandStep,
        {
            id: GenerationSteps.LOCATION,
            isComplete: false,
            data: null,
        } as BrandStep,
        {
            id: GenerationSteps.OPTIONS,
            isComplete: false,
            data: {
                id: GenerationMode.FULL,
                propertyName: '',
            },
        } as OptionsStep,
        {
            id: GenerationSteps.SELECT_CONTENT,
            isComplete: false,
            data: null,
        } as SelectContentStep,
        {
            id: GenerationSteps.DOWNLOAD_DOCUMENT,
            isComplete: false,
            data: null,
        } as DownloadDocumentStep,
    ],
};

export const documentGenerationStepsSlice = createSlice({
    name: 'document-generation-steps',
    initialState,
    reducers: {
        apply(state: DocumentGenerationStepsState, action: PayloadAction<DocumentGenerationStepsDispatchParamsApply>) {
            const currentStep = action.payload;

            const foundStepIndex = state.steps.findIndex((step) => step.id === currentStep.id);

            if (foundStepIndex !== -1) {
                const isEqual = isStepDataEqual({
                    prev: state.steps[foundStepIndex].data,
                    next: currentStep.data,
                    stepId: currentStep.id,
                });

                if (!isEqual) {
                    // clear all next
                    for (let i = foundStepIndex + 1; i < state.steps.length; i++) {
                        const step = state.steps[i];

                        const initialStep = initialState.steps[i];

                        // Ensure initialStep exists to prevent undefined access
                        if (initialStep) {
                            state.steps[i] = <DocumentGenerationStepsStep>{
                                id: step.id,
                                isComplete: initialStep.isComplete,
                                data: initialStep.data,
                            };
                        }
                    }

                    // skip all not required prev
                    for (let i = 0; i < foundStepIndex; i++) {
                        const step = state.steps[i];
                        const stepRule = stepsRules[step.id];

                        const initialStep = initialState.steps[i];

                        // Ensure initialStep exists to prevent undefined access
                        if (initialStep && !stepRule.isRequired && !step.data) {
                            state.steps[i] = <DocumentGenerationStepsStep>{
                                id: step.id,
                                isComplete: true,
                                data: initialStep.data,
                            };
                        }
                    }
                }

                const prevData = state.steps[foundStepIndex].data;

                // replace with new data
                state.steps.splice(foundStepIndex, 1, <DocumentGenerationStepsStep>{
                    id: currentStep.id,
                    isComplete: currentStep.isComplete,
                    data: currentStep.data ? {
                        ...prevData,
                        ...currentStep.data,
                    } : null,
                });
            }
        },
        reset() {
            return { ...initialState };
        },
    },
});
